import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import {
  ShoppingBag,
  TrendingUp,
  Package,
  Clock,
  CreditCard,
  Banknote,
  Star,
  Calendar,
  Zap,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  BarChart2,
  Inbox
} from 'lucide-react';

interface PlatformStats {
  platform: string;
  orders: number;
  revenue: number;
  avgOrderValue: number;
  percentage: number;
}

interface PaymentStats {
  type: string;
  orders: number;
  revenue: number;
  percentage: number;
}

interface TopProduct {
  name: string;
  quantity: number;
  revenue: number;
}

interface DailyTrend {
  date: string;
  orders: number;
  revenue: number;
}

interface TakeawayStats {
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
  comparison: {
    ordersChange: number;
    revenueChange: number;
    prevOrders: number;
    prevRevenue: number;
  };
  platforms: PlatformStats[];
  paymentBreakdown: PaymentStats[];
  topProducts: TopProduct[];
  dailyTrend: DailyTrend[];
  hourlyDistribution: { hour: string; orders: number; revenue: number }[];
  peakHours: { hour: string; orders: number }[];
  statusBreakdown: Record<string, number>;
}

interface TakeawayAnalyticsProps {
  dateRange: {
    from: string;
    to: string;
  };
}

const PLATFORM_COLORS: Record<string, string> = {
  internal: '#10b981',
  efood: '#dc2626',
  box: '#ea580c',
  wolt: '#2563eb'
};

const TakeawayAnalytics: React.FC<TakeawayAnalyticsProps> = ({ dateRange }) => {
  const { t } = useTranslation(['analytics', 'common']);
  const { store } = useAuth();
  const [stats, setStats] = useState<TakeawayStats | null>(null);
  const [loading, setLoading] = useState(true);

  const PLATFORM_NAMES: Record<string, string> = {
    internal: t('analytics:internalPlatform'),
    efood: 'e-food',
    box: 'Box',
    wolt: 'Wolt'
  };

  useEffect(() => {
    fetchTakeawayStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, dateRange]);

  const fetchTakeawayStats = async () => {
    if (!store?.id) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/stores/${store.id}/takeaway?from=${dateRange.from}&to=${dateRange.to}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch takeaway stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-t4">
        <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-t4" />
        <p>{t('analytics:takeaway.noTakeawayData')}</p>
      </div>
    );
  }

  const maxOrders = Math.max(...(stats.hourlyDistribution?.map(h => h.orders) || [1]));
  const maxDailyOrders = Math.max(...(stats.dailyTrend?.map(d => d.orders) || [1]));
  const maxProductRevenue = Math.max(...(stats.topProducts?.map(p => p.revenue) || [1]));

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'ready': return 'bg-blue-500';
      case 'preparing': return 'bg-yellow-500';
      case 'pending': return 'bg-slate-400';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-surface-4';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('analytics:takeaway.statusCompleted');
      case 'ready': return t('analytics:takeaway.statusReady');
      case 'preparing': return t('analytics:takeaway.statusPreparing');
      case 'pending': return t('analytics:takeaway.statusPending');
      case 'cancelled': return t('analytics:takeaway.statusCancelled');
      default: return status;
    }
  };

  const totalStatusOrders = Object.values(stats.statusBreakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards with Comparison */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 rounded-2xl border border-green-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center gap-2 mb-1.5">
            <ShoppingBag className="w-4 h-4" />
            {t('analytics:orders')}
          </p>
          <div className="text-xl sm:text-2xl font-bold text-t1">{stats.totalOrders}</div>
          {stats.comparison && (
            <div className={`flex items-center gap-1 text-xs mt-1 ${stats.comparison.ordersChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {stats.comparison.ordersChange >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              <span>{Math.abs(stats.comparison.ordersChange).toFixed(1)}%</span>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 rounded-2xl border border-emerald-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center gap-2 mb-1.5">
            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
            {t('analytics:revenue')}
          </p>
          <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
            {formatCurrency(stats.totalRevenue)}
          </div>
          {stats.comparison && (
            <div className={`flex items-center gap-1 text-xs mt-1 ${stats.comparison.revenueChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {stats.comparison.revenueChange >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              <span>{Math.abs(stats.comparison.revenueChange).toFixed(1)}%</span>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/5 rounded-2xl border border-purple-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center gap-2 mb-1.5">
            <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            {t('analytics:takeaway.avgValue')}
          </p>
          <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
            {formatCurrency(stats.avgOrderValue)}
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-2xl border border-amber-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center gap-2 mb-1.5">
            <Zap className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            {t('analytics:takeaway.peakHours')}
          </p>
          <div className="text-lg font-bold text-amber-700 dark:text-amber-400">
            {stats.peakHours?.slice(0, 2).map(h => h.hour.split(':')[0] + ':00').join(', ') || '-'}
          </div>
          <div className="text-xs text-t4 mt-1">
            {t('analytics:takeaway.ordersAtPeak', { count: stats.peakHours?.[0]?.orders || 0 })}
          </div>
        </div>
      </div>

      {/* Payment Breakdown & Order Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('analytics:takeaway.paymentMethods')}
            </h3>
          </div>
          <div className="p-5">
            {stats.paymentBreakdown && stats.paymentBreakdown.length > 0 ? (
              <div className="space-y-4">
                {stats.paymentBreakdown.map((payment) => (
                  <div key={payment.type} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {payment.type === 'cash' ? (
                        <Banknote className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      )}
                      <span className="font-medium capitalize text-t2">{payment.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-t1">{formatCurrency(payment.revenue)}</div>
                      <div className="text-xs text-t4">{payment.orders} {t('analytics:orders')} ({payment.percentage.toFixed(1)}%)</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-t3 dark:text-t4">{t('analytics:takeaway.noPaymentData')}</div>
            )}
          </div>
        </div>

        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {t('analytics:takeaway.orderStatus')}
            </h3>
          </div>
          <div className="p-5">
            {stats.statusBreakdown && Object.keys(stats.statusBreakdown).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.statusBreakdown).map(([status, count]) => (
                  <div key={status} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize text-t2">{getStatusLabel(status)}</span>
                      <span className="font-medium text-t1">{count} ({totalStatusOrders > 0 ? ((count / totalStatusOrders) * 100).toFixed(1) : 0}%)</span>
                    </div>
                    <div className="w-full bg-surface-2 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getStatusColor(status)}`}
                        style={{ width: `${totalStatusOrders > 0 ? (count / totalStatusOrders) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-t3 dark:text-t4">{t('analytics:takeaway.noStatusData')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1">{t('analytics:takeaway.ordersByPlatform')}</h3>
        </div>
        <div className="p-5">
          {stats.platforms && stats.platforms.length > 0 ? (
            <div className="space-y-4">
              {stats.platforms.map((platform) => (
                <div key={platform.platform} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: PLATFORM_COLORS[platform.platform] || '#6b7280' }}
                      />
                      <span className="font-medium text-t2">{PLATFORM_NAMES[platform.platform] || platform.platform}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-t3">{platform.orders} {t('analytics:orders')}</span>
                      <span className="font-semibold text-t1">{formatCurrency(platform.revenue)}</span>
                      <span className="text-t4">({platform.percentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-surface-2 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${platform.percentage}%`,
                        backgroundColor: PLATFORM_COLORS[platform.platform] || '#6b7280'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-t3 dark:text-t4">{t('analytics:takeaway.noProductData')}</div>
          )}
        </div>
      </div>

      {/* Top Products */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            {t('analytics:topProducts')}
          </h3>
        </div>
        <div className="p-5">
          {stats.topProducts && stats.topProducts.length > 0 ? (
            <div className="space-y-3">
              {stats.topProducts.map((product, index) => (
                <div key={product.name} className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-600 dark:text-purple-400 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-t1 truncate">{product.name}</span>
                      <div className="text-right text-sm">
                        <span className="font-semibold text-t1">{formatCurrency(product.revenue)}</span>
                        <span className="text-t4 ml-2">({product.quantity} {t('analytics:salesCount')})</span>
                      </div>
                    </div>
                    <div className="w-full bg-surface-2 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-purple-500"
                        style={{ width: `${(product.revenue / maxProductRevenue) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-t3 dark:text-t4">{t('analytics:takeaway.noProductData')}</div>
          )}
        </div>
      </div>

      {/* Daily Trend */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('analytics:takeaway.dailyTrend')}
          </h3>
        </div>
        <div className="p-5">
          {stats.dailyTrend && stats.dailyTrend.length > 0 ? (
            <>
              <div className="flex">
                <div className="flex flex-col justify-between h-44 pr-2 text-xs text-t4 w-8 shrink-0">
                  <span>{maxDailyOrders}</span>
                  <span>{Math.round(maxDailyOrders / 2)}</span>
                  <span>0</span>
                </div>
                <div className="flex items-end gap-[3px] h-44 flex-1 border-l border-b border-line pl-1 pb-1">
                  {stats.dailyTrend.map((day) => {
                    const barHeight = maxDailyOrders > 0 ? (day.orders / maxDailyOrders) * 100 : 0;
                    return (
                      <div key={day.date} className="flex-1 h-full flex flex-col items-center relative group cursor-pointer"
                        onClick={(e) => {
                          const el = e.currentTarget.querySelector('[data-tooltip]') as HTMLElement;
                          if (el) el.classList.toggle('!opacity-100');
                        }}
                      >
                        <div className="relative w-full flex items-end justify-center flex-1 min-h-0">
                          <div
                            className="w-full max-w-[28px] mx-auto bg-green-500 rounded-t-sm transition-all duration-300 group-hover:bg-green-400"
                            style={{ height: `${barHeight}%`, minHeight: day.orders > 0 ? '4px' : '0' }}
                          />
                          <div data-tooltip className="absolute -top-10 left-1/2 -translate-x-1/2 bg-surface-0/95 text-t1 text-xs px-2.5 py-1.5 rounded-lg shadow-lg border border-line opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none">
                            <div className="font-semibold">{day.orders} {t('analytics:orders')}</div>
                            <div className="text-t3">{formatCurrency(day.revenue)}</div>
                          </div>
                        </div>
                        <span className="text-[10px] text-t4 mt-1.5 leading-none">{formatDate(day.date)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {(() => {
                const bestDay = stats.dailyTrend.reduce((best, day) => day.orders > (best?.orders || 0) ? day : best, stats.dailyTrend[0]);
                const totalOrders = stats.dailyTrend.reduce((sum, d) => sum + d.orders, 0);
                const avgPerDay = stats.dailyTrend.length > 0 ? Math.round(totalOrders / stats.dailyTrend.length) : 0;
                return (
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-line/50 text-xs text-t3">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      <span>{t('analytics:takeaway.bestDay')} <span className="text-t1 font-medium">{formatDate(bestDay?.date)} ({bestDay?.orders} {t('analytics:takeaway.ordersShort')})</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <BarChart2 className="w-3.5 h-3.5 text-t4" />
                      <span>{t('analytics:takeaway.avgPerDay')} <span className="text-t1 font-medium">{avgPerDay}</span></span>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-t4">
              <div className="relative mb-4">
                <Calendar className="w-12 h-12 text-t4" />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-surface-2 rounded-full flex items-center justify-center">
                  <Inbox className="w-3 h-3 text-t4" />
                </div>
              </div>
              <p className="font-medium text-t3">{t('analytics:takeaway.noDailyData')}</p>
              <p className="text-xs text-t4 mt-1">{t('analytics:takeaway.dataWillAppearOrders')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Hourly Distribution */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('analytics:ordersPerHour')}
          </h3>
        </div>
        <div className="p-5">
          {stats.hourlyDistribution && stats.hourlyDistribution.length > 0 ? (
            <>
              <div className="flex">
                <div className="flex flex-col justify-between h-44 pr-2 text-xs text-t4 w-8 shrink-0">
                  <span>{maxOrders}</span>
                  <span>{Math.round(maxOrders / 2)}</span>
                  <span>0</span>
                </div>
                <div className="flex items-end gap-[2px] h-44 flex-1 border-l border-b border-line pl-1 pb-1">
                  {stats.hourlyDistribution.map((hour) => {
                    const isPeak = stats.peakHours?.some(p => p.hour === hour.hour);
                    const barHeight = maxOrders > 0 ? (hour.orders / maxOrders) * 100 : 0;
                    return (
                      <div key={hour.hour} className="flex-1 h-full flex flex-col items-center relative group cursor-pointer"
                        onClick={(e) => {
                          const el = e.currentTarget.querySelector('[data-tooltip]') as HTMLElement;
                          if (el) el.classList.toggle('!opacity-100');
                        }}
                      >
                        <div className="relative w-full flex items-end justify-center flex-1 min-h-0">
                          <div
                            className={`w-full max-w-[20px] mx-auto rounded-t-sm transition-all duration-300 ${isPeak ? 'bg-amber-500 group-hover:bg-amber-400' : 'bg-purple-500 group-hover:bg-purple-400'}`}
                            style={{ height: `${barHeight}%`, minHeight: hour.orders > 0 ? '3px' : '0' }}
                          />
                          <div data-tooltip className="absolute -top-10 left-1/2 -translate-x-1/2 bg-surface-0/95 text-t1 text-xs px-2.5 py-1.5 rounded-lg shadow-lg border border-line opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none">
                            <div className="font-semibold">{hour.hour}</div>
                            <div className="text-t3">{hour.orders} {t('analytics:orders')}</div>
                          </div>
                        </div>
                        <span className="text-[10px] text-t4 mt-1.5 leading-none">{hour.hour.split(':')[0]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between mt-4 pt-3 border-t border-line/50 text-xs text-t3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm bg-purple-500" />
                    <span>{t('analytics:takeaway.normalHours')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
                    <span>{t('analytics:takeaway.peakHours')}</span>
                  </div>
                </div>
                {stats.peakHours?.[0] && (
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />
                    <span>{t('analytics:takeaway.busiestHour')} <span className="text-t1 font-medium">{stats.peakHours[0].hour?.split(':')[0]}:00</span> ({stats.peakHours[0].orders} {t('analytics:takeaway.ordersShort')})</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-t4">
              <div className="relative mb-4">
                <Clock className="w-12 h-12 text-t4" />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-surface-2 rounded-full flex items-center justify-center">
                  <Inbox className="w-3 h-3 text-t4" />
                </div>
              </div>
              <p className="font-medium text-t3">{t('analytics:noHourlyData')}</p>
              <p className="text-xs text-t4 mt-1">{t('analytics:takeaway.dataWillAppearOrders')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeawayAnalytics;
